import React, { useState, useRef, useContext } from 'react';
import { Card, CardContent, Typography, Button, Box, TextField, Checkbox, FormControlLabel, Modal, CircularProgress } from '@mui/material';
import Logo from "../../images/logo/Logo.svg";
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { AuthContext } from '../../context/authcontext';
import { API_CONFIG } from "../../api/apiUrl";
import { motion } from 'framer-motion';

const contentVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 },
};

const contentTransition = {
    type: 'spring',
    stiffness: 120,
    damping: 20,
    duration: 0.3,
};


const Login = () => {
    const navigate = useNavigate();
    const authContext = useContext(AuthContext);
    if (!authContext) {
        throw new Error('Login must be used within an AuthProvider');
    }
    const { login } = authContext;

    const [mobile, setMobile] = useState<string>('+91-');
    const [mobileError, setMobileError] = useState<string>('');
    const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
    const [otpError, setOtpError] = useState<string>('');
    const [showModal, setShowModal] = useState<boolean>(false);
    const [rememberMe, setRememberMe] = useState<boolean>(true);
    const [getOtpLoading, setGetOtpLoading] = useState<boolean>(false);
    const [resendOtpLoading, setResendOtpLoading] = useState<boolean>(false);
    const [verifyOtpLoading, setVerifyOtpLoading] = useState<boolean>(false);
    const [resendCooldown, setResendCooldown] = useState<number>(0);
    const mobileInputRef = useRef<HTMLInputElement>(null);

    const handleMobileChange = (e: any) => {
        let value = e.target.value;
        if (!value.startsWith('+91-')) value = '+91-';
        const number = value.substring(4).replace(/\D/g, '').slice(0, 10);
        setMobile('+91-' + number);
        setMobileError(number.length === 10 ? '' : 'Mobile number must be 10 digits');
    };

    const handleOtpChange = (index: number) => (e: any) => {
        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setOtpError('');
        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-input-${index + 1}`);
            if (nextInput) nextInput.focus();
        }
    };

    const handleOtpKeyDown = (index: number, e: any) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-input-${index - 1}`);
            if (prevInput) prevInput.focus();
        }
    };

    const startResendCooldown = () => {
        setResendCooldown(60);
        const interval = setInterval(() => {
            setResendCooldown(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const getOtp = () => {
        const number = mobile.substring(4);
        if (number.length !== 10) {
            setMobileError('Mobile number must be 10 digits');
            return;
        }
        setGetOtpLoading(true);
        axios.post(API_CONFIG.GET_OTP, { mobile: parseInt(number) })
            .then(() => {
                setShowModal(true);
                startResendCooldown();
                toast.success('OTP sent!');
            })
            .catch(err => {
                toast.error(err.response?.data?.detail || 'Failed to send OTP');
            })
            .finally(() => setGetOtpLoading(false));
    };

    const handleResendOtp = () => {
        if (resendCooldown > 0) return;
        setResendOtpLoading(true);
        const number = mobile.substring(4);
        axios.post(API_CONFIG.GET_OTP, { mobile: parseInt(number) })
            .then(() => {
                startResendCooldown();
                toast.success('OTP resent!');
            })
            .catch(err => {
                toast.error(err.response?.data?.detail || 'Failed to resend OTP');
            })
            .finally(() => setResendOtpLoading(false));
    };

    const fetchUserProfile = async (token: string) => {
        const response = await axios.get(API_CONFIG.USER_PROFILES, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    };

    const verifyOtp = () => {
        if (!otp.every(digit => digit)) {
            toast.error('Please enter all 6 digits');
            return;
        }
        setVerifyOtpLoading(true);
        const number = mobile.substring(4);
        axios.post(API_CONFIG.VERIFY_OTP, {
            mobile: parseInt(number),
            otp: parseInt(otp.join('')),
            message: "string"
        })
            .then(async res => {
                const idToken = res.data?.id_token || res.data?.token;
                if (!idToken) {
                    toast.error('No token received');
                    return;
                }
                localStorage.setItem('id_token', idToken);
                axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
                try {
                    const userProfile = await fetchUserProfile(idToken);
                    login(idToken);
                    localStorage.setItem('userLogin', userProfile.user.login);
                    toast.success('OTP verified!');
                    setShowModal(false);
                    setOtpError('');
                    const hasPlots = userProfile.plots && userProfile.plots.length > 0;
                    const isProvider = userProfile.authorities.includes('ROLE_PROVIDER');
                    navigate(isProvider || hasPlots ? '/booking' : '/', {
                        state: { userId: userProfile.user.id, mobile, userProfile }
                    });
                } catch {
                    toast.error('Failed to fetch user profile');
                }
            })
            .catch(err => {
                toast.error( 'Invalid OTP');
            })
            .finally(() => setVerifyOtpLoading(false));
    };

    return (

        <motion.div
            variants={contentVariants}
            initial="initial"
            animate="in"
            exit="out"
            transition={contentTransition}
        >
            <Box sx={styles.mainContainer}>
                <Toaster position="top-right" />

                <Card sx={styles.card}>
                    <CardContent>
                        <Box sx={styles.logoContainer}>
                            <img src={Logo} alt="Logo" style={styles.logo} />
                        </Box>
                        <Box sx={styles.buttonContainer}>
                            <Button sx={styles.SignInButton}>Sign In</Button>
                            <Link to='/signup'><Button sx={styles.SignUpButton}>Sign Up</Button></Link>
                        </Box>
                        <Box>
                            <Typography sx={styles.inputLabel}>Mobile Number</Typography>
                            <TextField
                                fullWidth
                                placeholder="+91-9999999999"
                                variant="outlined"
                                size="small"
                                value={mobile}
                                onChange={handleMobileChange}
                                inputRef={mobileInputRef}
                                sx={styles.textField}
                                error={!!mobileError}
                                helperText={mobileError}
                            />
                        </Box>
                        <Box sx={styles.checkboxContainer}>
                            <FormControlLabel
                                control={<Checkbox checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} sx={styles.checkbox} />}
                                label={<Typography sx={styles.checkboxLabel}>Remember me</Typography>}
                            />
                        </Box>
                        <Box sx={styles.submitContainer}>
                            <Button
                                sx={styles.submitButton}
                                onClick={getOtp}
                                disabled={getOtpLoading || mobile.length !== 14 || !!mobileError}
                            >
                                {getOtpLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Get OTP'}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
                <Modal open={showModal} onClose={() => setShowModal(false)}>
                    <Box sx={modalStyles}>
                        <Typography variant="h5" sx={{ mb: 1, textAlign: 'center', fontWeight: 600, color: '#333' }}>
                            Verify Your OTP
                        </Typography>
                        <Typography sx={{ mb: 2, textAlign: 'center', color: '#757575', fontSize: { xs: '12px', md: '14px' } }}>
                            Enter the 6-digit code sent to your mobile number or registered email
                        </Typography>
                        <Box sx={{ display: 'flex', gap: { xs: 1, md: 1.5 }, justifyContent: 'center', mb: 3 }}>
                            {[0, 1, 2, 3, 4, 5].map(i => (
                                <TextField
                                    key={i}
                                    id={`otp-input-${i}`}
                                    value={otp[i]}
                                    onChange={handleOtpChange(i)}
                                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                                    inputProps={{
                                        maxLength: 1,
                                        style: { textAlign: 'center', fontWeight: 500 },
                                        inputMode: "numeric" as const
                                    }}
                                    variant="outlined"
                                    sx={{
                                        width: { xs: '36px', md: '48px' },
                                        height: { xs: '36px', md: '48px' },
                                        fontSize: { xs: '18px', md: '24px' },
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            height: '100%',
                                            bgcolor: '#f5f5f5',
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A', borderWidth: '2px' }
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, md: 2 } }}>
                            <Button
                                onClick={handleResendOtp}
                                sx={styles.resendButton}
                                disabled={resendCooldown > 0 || resendOtpLoading}
                            >
                                {resendOtpLoading ? <CircularProgress size={24} color="inherit" /> : (resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP')}
                            </Button>
                            <Button
                                onClick={verifyOtp}
                                disabled={!otp.every(digit => digit) || verifyOtpLoading}
                                sx={styles.submitButton}
                            >
                                {verifyOtpLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Verify OTP'}
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            </Box>
        </motion.div>
    );
};

const styles: any = {
    mainContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 1, md: 2 },
        minHeight: '100vh'
    },
    card: {
        boxShadow: "0px 0px 4px 0px #00000033",
        width: { xs: '100%', md: '450px' },
        maxWidth: 450,
        p: { xs: 1.5, md: 2.5 },
        borderRadius: '20px',
        margin: '0 auto'
    },
    logoContainer: { display: 'flex', justifyContent: 'center', mb: { xs: 2, md: 2.5 } },
    logo: { width: { xs: '90px', md: '120px' } },
    buttonContainer: { display: 'flex', justifyContent: 'center', mb: { xs: 2, md: 2.5 }, gap: { xs: 2, md: 4 } },
    SignInButton: { textTransform: 'capitalize', fontSize: { xs: '14px', md: '15px' }, fontWeight: 500, borderRadius: '24px', color: 'white', bgcolor: '#B69D74', py: 0.5, px: { xs: 3, md: 4 } },
    SignUpButton: { textTransform: 'capitalize', fontSize: { xs: '14px', md: '15px' }, fontWeight: 500, borderRadius: '24px', border: '1px solid black', color: '#000', borderColor: '#000', '&:hover': { borderColor: '#666', bgcolor: 'transparent' }, px: { xs: 3, md: 4 }, py: 0.5 },
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '50px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' }
        },
        fontSize: { xs: '13px', md: '14px' }
    },
    inputLabel: { fontSize: { xs: '13px', md: '14px' }, fontWeight: 400, mb: { xs: 0.75, md: 1 }, color: '#212121' },
    checkboxContainer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 0.75, md: 1.5 } },
    checkbox: { color: '#B69D74', '&.Mui-checked': { color: '#B69D74' } },
    checkboxLabel: { fontSize: { xs: '13px', md: '14px' }, fontWeight: 400, color: '#212121', ml: 0.5 },
    submitContainer: { display: 'flex', justifyContent: 'center', mt: { xs: 2, md: 2.5 } },
    submitButton: { textTransform: 'capitalize', fontSize: { xs: '16px', md: '16px' }, fontWeight: 600, borderRadius: '28px', color: 'white', bgcolor: '#89652A', py: 0.7, px: { xs: 4, md: 6 }, '&:hover': { bgcolor: '#6b4e20' }, '&:disabled': { bgcolor: '#a88b5e' } },
    resendButton: { textTransform: 'capitalize', fontSize: { xs: '13px', md: '14px' }, fontWeight: 400, color: '#212121', py: 0.5, px: { xs: 2, md: 3 } }
};

const modalStyles = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '85%', sm: '85%', md: '85%' },
    maxWidth: '350px',
    bgcolor: 'white',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    p: { xs: 2.5, md: 3 },
    border: '1px solid #e0e0e0'
};

export default Login;